import { BeakerIcon, ChatBubbleLeftRightIcon, MinusIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import React, { useState } from 'react'
import { Environment } from '../../../environment.js'
import type { IProjectSample, ProjectOrder } from '../../../Model/Project/IProjectSample.js'
import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.js'
import { ProjectSampleTable } from './ProjectSampleTable.js'
import { useNavigate } from 'react-router-dom'
import SamplesMap from 'View/Common/SamplesMap.tsx'
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline/index.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { determineSampleOperations } from '../HomePage/Modals/DetermineSampleOperations.js'
import { mapToBatches } from '../HomePage/Modals/MapToBatches.js'
import Actions from '../../../Flux/index.js'
import { stringifyError, stringifyWarning } from '../HomePage/Modals/CSVModalTypes.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../Model/Message/IMessage.js'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  getAuthenticatedUser,
  selectAddingProjectSamples,
  selectCurrentSamples,
  selectProjectOrders,
  selectSamplesLoading,
  useGetAuthenticatedUser
} from '../../../Flux/Selector/index.js'
import type { State } from '../../../Flux/Slice/index.js'
import type { IUser } from '../../../Model/User/IUser.js'
import { addSamplesDisabledReason } from './ProjectSampleWidget.logic.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { useConfirm } from '../../Component/ConfirmProvider.js'
import { handleCSVUpload } from '../OrderManagementPage/FileHandling/CSVHelper.js'
import { useModal } from '../../Modal/UseModal.js'
import type { ParseCSVModalResult } from '../HomePage/Modals/ParseCSVModal.js'
import { canUseCsvGridEditor, isChatBeta } from '../../../Model/index.js'
import { buildProjectSampleCsvTable } from '../HomePage/Modals/PoolCsvTable.js'
import { trackProjectAction } from '../../../Services/Telemetry/index.js'

type ProjectSampleWidgetProps = {
  samples: IProjectSample[] | 'LOADING'
  readOnly: boolean
  project: IEvaluationProject
  orders: ProjectOrder[]
}

/**
 * A project spans orders from several organisations, but metadata may only be written to samples
 * from the user's own orders. Rows outside this set come back from `determineSampleOperations` as
 * `WarnUnmatchedSequenceId`, so the grid must be built from exactly the same subset the upload
 * path writes through — otherwise every foreign row turns into a warning on submit.
 */
const ownSamplesOf = (samples: IProjectSample[] | 'LOADING', orders: ProjectOrder[], user: IUser | null | undefined): IProjectSample[] => {
  if (samples === 'LOADING' || !user) {
    return []
  }
  const ordersById = Object.fromEntries(orders.map((order) => [order.anonymousOrder.id, order]))
  return samples.filter((x) => {
    const fullOrder = x.sample.orderId ? ordersById[x.sample.orderId]?.fullOrder : undefined
    return fullOrder?.customer === user.org.id || fullOrder?.lab === user.org.id
  })
}

export const ProjectSampleWidget = ({ samples, readOnly, project, orders }: ProjectSampleWidgetProps) => {
  const [showRemoveButtons, setShowRemoveButtons] = useState(false)
  const [selectedSamples, setSelectedSamples] = useState<number[]>([])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const { confirm } = useConfirm()
  const { openModal } = useModal()
  const store = useStore<typeof State>()
  const samplesLoading = useSelector(selectSamplesLoading)
  const addingSamples = useSelector(selectAddingProjectSamples)
  const addDisabledReason = addSamplesDisabledReason({ samples, samplesLoading, locked: !!project.lock, adding: addingSamples })

  const chatBetaAccess = isChatBeta(user)
  const canEditCsvInGrid = canUseCsvGridEditor(user)

  const ownSamples = ownSamplesOf(samples, orders, user)

  /* Runs when the import modal closes, possibly after other saves or a reload: read the store now, not
     the props of the render that opened the modal. */
  const csvOk = (result: ParseCSVModalResult) => {
    const state = store.getState()
    const currentSamples = selectCurrentSamples(state) ?? []
    const currentOrders = selectProjectOrders(state)
    const currentUser = getAuthenticatedUser(state)
    if (currentSamples === 'LOADING' || currentOrders === 'LOADING' || !currentUser) {
      return
    }
    const ownSamples = ownSamplesOf(currentSamples, currentOrders, currentUser)

    try {
      console.info('own samples are', ownSamples)
      console.info(
        'as samples',
        ownSamples.map((x) => x.sample)
      )
      console.info(
        'as metadata',
        ownSamples.map((x) => x.sample.sampleMetadata).filter((x) => !!x)
      )
      const ops = determineSampleOperations(
        ownSamples.map((x) => x.sample),
        ownSamples.map((x) => x.sample.sampleMetadata).filter((x) => !!x),
        result.data,
        result.flags,
        'UPDATE_METADATA'
      )
      console.info('ops are', ops)

      if (ops.errors.length <= 0) {
        const handler = () => {
          const batches = mapToBatches(ops)

          batches.forEach((batch) => dispatch(Actions[projectSlice.name].batchUpdateSamples(batch)))
        }
        if (ops.warnings.length <= 0) {
          handler()
        } else {
          confirm(
            {
              title: 'Warnings where encountered while processing the CSV',
              message: (
                <>
                  <p>Do you want to apply these changes?</p>
                  <ul className={'list-disc'}>
                    {ops.warnings.map((w, index) => (
                      <li key={index} className={''}>
                        {stringifyWarning(w)}
                      </li>
                    ))}
                  </ul>
                </>
              )
            },
            handler
          ).then(() => console.log('fulfilled promise from confirm'))
        }
      } else {
        console.error(ops.errors)
        ops.errors.forEach((x) => dispatch(addMessage(errorMessage(stringifyError(x)))))
      }
    } catch (e) {
      console.error(e)
      if (e !== undefined && e !== null && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
        dispatch(addMessage(errorMessage(e.message)))
      } else {
        dispatch(addMessage(errorMessage('An unknown error occurred while processing the CSV. Please try again later.')))
      }
    }
  }

  const openEditedCsvFlow = () => {
    const table = buildProjectSampleCsvTable(ownSamples.map((x) => x.sample))
    const total = samples === 'LOADING' ? 0 : samples.length

    openModal(
      'editCsvModal',
      {
        contextLabel: `Project ${project.id}`,
        csvMode: 'UPDATE_METADATA',
        initialColumns: table.columns,
        initialRows: table.rows,
        notice:
          table.rows.length < total
            ? `Showing ${table.rows.length} of ${total} project samples — metadata can only be edited on samples from your own orders.`
            : undefined
      },
      (editResult) => {
        if (!editResult) return

        openModal(
          'parseCSV',
          {
            rawCSVData: editResult.rows,
            fromGrid: true,
            mode: 'UPDATE_METADATA'
          },
          (parseResult) => {
            if (!parseResult) return
            csvOk(parseResult)
          }
        )
      }
    )
  }

  return (
    <>
      <div className="text-xl mb-3">Samples {samplesLoading && <span className={'text-sm italic'}>Loading samples...</span>}</div>
      {/* Sample Pools Filter */}
      <div className="flex mb-3">
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'View analysis data'}
          disabledTooltip={'Exit draft mode for up to date data'}
          disabled={project.draftMode}
        >
          <button
            disabled={project.draftMode}
            role="button"
            onClick={() => navigate(`/projects/${project.id}/otus`)}
            className="btn btn-primary btn-sm ml-2"
          >
            <BeakerIcon className="w-4" /> Analysis
          </button>
        </TooltipTrigger>
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'Show Krona Charts'}
          disabledTooltip={'Exit draft mode for up to date data'}
          disabled={project.draftMode}
        >
          <button
            disabled={project.draftMode}
            role="button"
            onClick={() => openModal('krona', { project })}
            className="btn btn-primary btn-sm ml-2"
          >
            <BeakerIcon className="w-4" /> Krona Analysis
          </button>
        </TooltipTrigger>
        <TooltipTrigger
          classNames={'inline-flex'}
          tooltip={'Visualize your project'}
          disabledTooltip={'Exit draft mode for up to date data'}
          disabled={project.draftMode}
        >
          <button
            disabled={project.draftMode}
            data-testid="vis-workspace"
            role="button"
            onClick={() => navigate(`/project/workspace/${project.id}`)}
            className="btn btn-primary btn-sm ml-2"
          >
            <BeakerIcon className="w-4" /> View Plots
          </button>
        </TooltipTrigger>
        {/* Gated on CHAT_BETA — keep in sync with the endpoint's role check. */}
        {Environment.CHAT_ENABLED && chatBetaAccess && (
          <TooltipTrigger
            classNames={'inline-flex'}
            tooltip={'Ask questions about your data'}
            disabledTooltip={'Exit draft mode for up to date data'}
            disabled={project.draftMode}
          >
            <button
              disabled={project.draftMode}
              role="button"
              onClick={() => navigate(`/projects/${project.id}/chat`)}
              className="btn btn-primary btn-sm ml-2"
            >
              <ChatBubbleLeftRightIcon className="w-4" /> Ask AI
            </button>
          </TooltipTrigger>
        )}

        {!readOnly && project && (
          <>
            <TooltipTrigger
              classNames={'inline-flex'}
              tooltip={'Add samples from an order'}
              disabledTooltip={addDisabledReason}
              disabled={!!addDisabledReason}
            >
              <button
                data-testid={'add-samples'}
                role="button"
                className="btn justify-end btn-sm ml-2"
                disabled={!!addDisabledReason}
                onClick={() => openModal('importSamplesProject', { project })}
              >
                <PlusIcon className="w-4" /> Add Samples
              </button>
            </TooltipTrigger>
            <button
              data-testid="edit-samples"
              role="button"
              className="btn justify-end btn-sm ml-2"
              onClick={() => {
                setShowRemoveButtons((prev) => !prev)
                setSelectedSamples([])
              }}
            >
              <MinusIcon className="w-4" /> {showRemoveButtons ? 'Stop Editing' : 'Edit Samples'}
            </button>
            {showRemoveButtons && selectedSamples.length > 0 && (
              <button
                data-testid="remove-selected-samples"
                role="button"
                className="btn btn-error justify-end btn-sm ml-2"
                onClick={() => {
                  confirm(
                    {
                      title: 'Remove Selected Samples',
                      message: `Are you sure you want to remove ${selectedSamples.length} selected sample${selectedSamples.length > 1 ? 's' : ''}?`
                    },
                    () => {
                      dispatch(
                        Actions[projectSlice.name].deleteMultipleProjectSamplesRequest({
                          projectId: project.id,
                          projectSampleIds: selectedSamples
                        })
                      )
                      setSelectedSamples([])
                    }
                  )
                }}
              >
                <TrashIcon className="w-4" /> Remove Selected ({selectedSamples.length})
              </button>
            )}
            <TooltipTrigger tooltip={'Update metadata of samples. You can only upload metadata for samples coming from your own orders.'}>
              <label role="button" className={`btn justify-end btn-sm ml-2`} htmlFor={`uploadPrimerInfoBtn${project.id}`}>
                <input
                  data-testid="upload-csv"
                  type="file"
                  hidden
                  id={`uploadPrimerInfoBtn${project.id}`}
                  accept=".csv"
                  disabled={samples.length === 0}
                  onChange={async (event) => {
                    await handleCSVUpload(event, confirm, dispatch, (data) =>
                      openModal(
                        'parseCSV',
                        {
                          rawCSVData: data.data as Record<string, string>[],
                          mode: 'UPDATE_METADATA'
                        },
                        (result) => csvOk(result)
                      )
                    )
                  }}
                />
                <DocumentArrowUpIcon className="w-4 h-4" />
                Update Sample Metadata
              </label>
            </TooltipTrigger>
            {canEditCsvInGrid && (
              <TooltipTrigger tooltip="Edit the metadata of your own samples in a grid. On submit the edited data goes through the same processing flow as a CSV upload.">
                <button
                  type="button"
                  data-testid="editCSV"
                  disabled={samplesLoading || ownSamples.length === 0}
                  className="btn btn-sm btn-outline btn-secondary justify-end ml-2"
                  onClick={() => {
                    trackProjectAction('edit_data_beta', project.id)
                    openEditedCsvFlow()
                  }}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit Data
                </button>
              </TooltipTrigger>
            )}
          </>
        )}
      </div>
      <ProjectSampleTable
        data={samples}
        editing={showRemoveButtons}
        projectId={project.id}
        orders={orders}
        selectedSamples={selectedSamples}
        onSelectionChange={setSelectedSamples}
      />
      {samples === 'LOADING' ? (
        'loading map...'
      ) : (
        <div className="p-4 w-full h-[50vh]">
          <SamplesMap
            className="w-full h-full"
            samples={samples.map((s) => s.sample)}
            metaData={samples.map((s) => s.sample.sampleMetadata).filter((s) => !!s)}
          />
        </div>
      )}
    </>
  )
}
